import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";
import {
  quandoLegivel,
  ESCOPOS_DE_TOKEN,
  ROTULO_DO_ESCOPO_DE_TOKEN,
  useCriarTokenDeApi,
  useOrganizacoesParaAutomacao,
  useRevogarTokenDeApi,
  useTokensDeApi,
} from "../../nucleo/automacao";
import { mensagemDoConsole } from "../../nucleo/console";
import { CaixaDeMarcar, CampoDeEscolha } from "./apoio";
import { SegredoMostradoUmaVez } from "./SegredoMostradoUmaVez";

export function PainelDeTokens() {
  const tokens = useTokensDeApi();
  const organizacoes = useOrganizacoesParaAutomacao();
  const criar = useCriarTokenDeApi();
  const revogar = useRevogarTokenDeApi();
  const [nome, setNome] = useState("");
  const [organizacaoId, setOrganizacaoId] = useState("");
  const [escopos, setEscopos] = useState<string[]>(["chamados:ler"]);
  const [problema, setProblema] = useState<string | null>(null);
  const [criado, setCriado] = useState<{ nome: string; token: string } | null>(null);

  const nomesDasOrganizacoes = useMemo(
    () => Object.fromEntries((organizacoes.data ?? []).map((o) => [o.id, o.nome_fantasia ?? o.razao_social])),
    [organizacoes.data],
  );

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const erro = nome.trim().length < 2 ? "Dê um nome à integração." : escopos.length === 0 ? "Escolha ao menos um escopo." : null;
    setProblema(erro);
    if (erro) return;
    criar.mutate(
      { nome: nome.trim(), organizacao_id: organizacaoId || null, escopos },
      {
        onSuccess: (resposta) => {
          setCriado({ nome: resposta.nome, token: resposta.token });
          setNome("");
        },
      },
    );
  }

  const falha = criar.error ?? revogar.error ?? tokens.error;

  return (
    <div>
      {criado ? (
        <SegredoMostradoUmaVez
          titulo={`Token da integração ${criado.nome}`}
          valor={criado.token}
          rotuloDoBotao="Copiar token"
          aoFechar={() => setCriado(null)}
          data-testid="token-criado"
        />
      ) : null}
      <Cartao titulo="Novo token de API" className="mb-4">
        <form onSubmit={submeter} data-testid="formulario-de-token" noValidate>
          <Campo rotulo="Nome da integração" value={nome} onChange={(e) => setNome(e.target.value)} />
          <CampoDeEscolha
            rotulo="Organização do token"
            valor={organizacaoId}
            aoMudar={setOrganizacaoId}
            vazio="Interna (GEOvendas)"
            opcoes={(organizacoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome_fantasia ?? o.razao_social }))}
          />
          <p className="campo__rotulo">Escopos</p>
          {ESCOPOS_DE_TOKEN.map((escopo) => (
            <CaixaDeMarcar
              key={escopo}
              rotulo={ROTULO_DO_ESCOPO_DE_TOKEN[escopo] ?? escopo}
              marcada={escopos.includes(escopo)}
              aoMudar={(marcada) => setEscopos((atual) => (marcada ? [...atual, escopo] : atual.filter((e) => e !== escopo)))}
            />
          ))}
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <Botao type="submit" disabled={criar.isPending}>{criar.isPending ? "Criando…" : "Criar token"}</Botao>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-tokens">
        <h2 className="titulo-secao" id="titulo-tokens">Tokens de API</h2>
        {tokens.isPending ? <p role="status">Carregando tokens…</p> : null}
        {tokens.data && tokens.data.length === 0 ? <Aviso tom="marca">Nenhum token criado.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-tokens">
          {tokens.data?.map((token) => (
            <Cartao
              key={token.id}
              titulo={token.nome}
              data-testid={`token-${token.nome}`}
              acessorio={<Chip estado={token.revogado_em ? "erro" : "ok"}>{token.revogado_em ? "Revogado" : "Ativo"}</Chip>}
            >
              <p>
                <code>{token.prefixo}…</code> ·{" "}
                {token.organizacao_id ? nomesDasOrganizacoes[token.organizacao_id] ?? "Organização do cliente" : "Interna (GEOvendas)"}
              </p>
              <p className="nota-rodape">
                {token.escopos.map((e) => ROTULO_DO_ESCOPO_DE_TOKEN[e] ?? e).join(", ")} ·{" "}
                {token.ultimo_uso_em ? `último uso em ${quandoLegivel(token.ultimo_uso_em)}` : "nunca usado"}
              </p>
              {!token.revogado_em ? (
                <Botao type="button" variante="fantasma" className="mt-2" aria-label={`Revogar token ${token.nome}`} onClick={() => revogar.mutate(token.id)}>
                  Revogar
                </Botao>
              ) : null}
            </Cartao>
          ))}
        </div>
      </section>
    </div>
  );
}
